import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from './core/services/auth.service';
import { ThemeService } from './core/services/theme.service';
import { BackendStorageService } from './core/services/backend-storage.service';
import { PlaylistGenerationSettingsService } from './core/services/playlist-generation-settings.service';
import { ViewStateService, ViewState } from './core/services/view-state.service';
import { Router } from '@angular/router';
import { VoiceSpeechDialogComponent } from './features/quick-play/components/voice-speech/voice-speech-dialog.component';
import { BugReportDialogComponent } from './shared/components/bug-report-dialog/bug-report-dialog.component';
import { DynamicPlaylistChainService } from './core/services/dynamic-playlist-chain.service';
import { VersionCheckService } from './core/services/version-check.service';
import { StallMonitorService } from './core/services/stall-monitor.service';
import { APP_VERSION } from './version';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatSidenav;
  
  title = 'Music Served';
  version = APP_VERSION;
  isAuthenticated = false;
  isHandset = false;
  viewState: ViewState = { isSimpleView: false };
  private destroy$ = new Subject<void>();
  private dynamicPlaylistChain = inject(DynamicPlaylistChainService);
  private versionCheck = inject(VersionCheckService);
  private stallMonitor = inject(StallMonitorService);

  constructor(
    private authService: AuthService,
    public themeService: ThemeService,
    private breakpointObserver: BreakpointObserver,
    private backendStorageService: BackendStorageService,
    private playlistSettingsService: PlaylistGenerationSettingsService,
    private viewStateService: ViewStateService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    // Reload stale bundles automatically (deferred while music plays).
    this.versionCheck.start();

    // Instrument the SDK's media element to explain audio stalls.
    this.stallMonitor.start();

    // Chain dynamic playlists (regenerate + continue when one ends).
    this.dynamicPlaylistChain.start();

    // Check auth status immediately on app init
    console.log('App initializing, checking auth status...');
    this.authService.checkAuthStatus().pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      isAuthenticated => {
        console.log('Initial auth check result:', isAuthenticated);
      },
      error => {
        console.error('Initial auth check failed:', error);
      }
    );
    
    // Subscribe to authentication status changes
    this.authService.authStatus$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isAuthenticated => {
      this.isAuthenticated = isAuthenticated;
      
      // Initialize backend storage when user is authenticated
      if (isAuthenticated) {
        this.initializeBackendStorage();
      }
    });

    // Subscribe to breakpoint changes for responsive behavior
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.isHandset = result.matches;
    });

    // Subscribe to view state changes
    this.viewStateService.viewState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(viewState => {
      this.viewState = viewState;
      
      // Navigate to playlists when entering simple view
      if (viewState.isSimpleView && this.isAuthenticated) {
        this.router.navigate(['/playlists'], { replaceUrl: true });
      }
    });

    // Wait for theme service to initialize
    this.themeService.waitForInitialization();
  }

  private async initializeBackendStorage(): Promise<void> {
    try {
      // Load all user data (backend will handle initialization if needed)
      await this.backendStorageService.getAllUserData().toPromise();
      
      console.log('Backend storage initialized successfully');
    } catch (error) {
      console.warn('Backend storage initialization failed:', error);
      // This is not a critical error - the app should still work with defaults
    }
  }

  onMenuItemClick(): void {
    // Only close drawer on mobile devices
    if (this.isHandset && this.drawer) {
      this.drawer.close();
    }
  }

  toggleView(): void {
    this.viewStateService.toggleView();
  }

  openVoiceCommands(): void {
    const dialogRef = this.dialog.open(VoiceSpeechDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: { autoStart: true },
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        console.log('Voice command processed successfully:', result);
        // Show a loading indicator while navigating
        this.snackBar.open('Loading your playlist...', '', {
          duration: 3000,
          panelClass: ['loading-snackbar']
        });
      }
    });
  }

  openBugReport(): void {
    this.dialog.open(BugReportDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      autoFocus: 'textarea',
      disableClose: false
    });
  }

  get showSidebarAndProfile(): boolean {
    return !this.viewState.isSimpleView;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}